import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import QRCode from "react-qr-code";
import { Download, Scan, Sparkles } from "lucide-react";

/**
 * Premium "Scan to Order" QR section.
 * - Branded dark luxury card with animated scan line
 * - Dynamic QR pointing to the menu landing
 * - Downloadable PNG for tables / packaging / flyers
 */
export const QRScanSection = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const qrWrapRef = useRef(null);
    const [size, setSize] = useState("table");

    const targetUrl = typeof window !== "undefined" ? `${window.location.origin}/menu` : "/menu";

    const download = (filename) => {
        const svg = qrWrapRef.current?.querySelector("svg");
        if (!svg) return;
        const data = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const PX = 1024;
        canvas.width = PX; canvas.height = PX;
        const ctx = canvas.getContext("2d");
        const img = new Image();
        const blob = new Blob([data], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        img.onload = () => {
            ctx.fillStyle = "#0B0B0B"; ctx.fillRect(0, 0, PX, PX);
            ctx.drawImage(img, PX * 0.12, PX * 0.12, PX * 0.76, PX * 0.76);
            URL.revokeObjectURL(url);
            const link = document.createElement("a");
            link.download = filename;
            link.href = canvas.toDataURL("image/png");
            link.click();
        };
        img.src = url;
    };

    return (
        <section ref={ref} data-testid="qr-section" className="relative py-24 sm:py-32 px-5 sm:px-10">
            <div className="absolute inset-0 -z-0 bg-gradient-to-b from-black via-[#0c0a08] to-black" />
            <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
                {/* Copy side */}
                <motion.div
                    initial={{ opacity: 0, x: -40 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.8, ease: [0.2,0.9,0.2,1] }}
                >
                    <p className="font-display text-xs tracking-[0.5em] ember-text mb-3">// SCAN TO ORDER</p>
                    <h2 className="font-display text-5xl sm:text-7xl leading-[0.95]">
                        ONE SCAN.<br /><span className="ember-text">FULL MENU.</span>
                    </h2>
                    <p className="text-white/65 leading-relaxed max-w-md mt-5">
                        Σκάναρε από το τραπέζι, το packaging ή ένα flyer και άνοιξε το menu σε ένα δευτερόλεπτο. Καμία εφαρμογή, καμία εγγραφή.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-2">
                        {[
                            { id: "table",     label: "Τραπέζι" },
                            { id: "packaging", label: "Packaging" },
                            { id: "flyer",     label: "Flyer" },
                        ].map((p) => {
                            const on = size === p.id;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`qr-preset-${p.id}`}
                                    onClick={() => setSize(p.id)}
                                    className={`relative px-4 h-10 rounded-full font-display tracking-widest text-xs transition ${
                                        on ? "text-black" : "text-white/65 hover:text-white border border-white/15"
                                    }`}
                                >
                                    {on && <motion.span layoutId="qr-preset-pill" className="absolute inset-0 -z-10 rounded-full ember-gradient" />}
                                    {p.label.toUpperCase()}
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                        {[
                            { t: "INSTANT", s: "open" },
                            { t: "NO APP", s: "needed" },
                            { t: "MOBILE", s: "optimized" },
                        ].map((f, i) => (
                            <div key={i} className="glass-burger rounded-2xl p-3 border border-white/8">
                                <p className="font-display text-sm tracking-widest ember-text">{f.t}</p>
                                <p className="text-[10px] text-white/45 mt-0.5">{f.s}</p>
                            </div>
                        ))}
                    </div>

                    <motion.button
                        onClick={() => download(`grill-bros-qr-${size}.png`)}
                        whileTap={{ scale: 0.96 }}
                        data-testid="qr-download"
                        className="magnet-cta primary mt-10 h-12 px-6 text-xs"
                    >
                        <Download size={14} /> Download QR · {size.toUpperCase()}
                    </motion.button>
                </motion.div>

                {/* QR Card */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, delay: 0.2, ease: [0.2,0.9,0.2,1] }}
                    className="relative mx-auto w-full max-w-sm"
                >
                    <div className="absolute -inset-10 bg-[var(--burger-orange)]/15 blur-3xl rounded-full -z-10" />
                    <div
                        data-testid="qr-card"
                        className="relative aspect-[4/5] rounded-[36px] p-7 glass-burger border border-[var(--burger-orange)]/30 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                    >
                        {/* Top branding */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="h-7 w-7 grid place-items-center rounded-lg ember-gradient">
                                    <Sparkles size={12} className="text-black" />
                                </span>
                                <div>
                                    <p className="font-display text-base tracking-[0.2em] leading-none">GRILL BROS</p>
                                    <p className="text-[8px] font-mono text-white/45 tracking-widest mt-0.5">// SMASH BURGERS</p>
                                </div>
                            </div>
                            <span className="text-[8px] font-mono text-white/40 tracking-widest">EST · 2013</span>
                        </div>

                        {/* QR with scan line */}
                        <div ref={qrWrapRef} className="relative mt-5 mx-auto w-[80%] aspect-square rounded-2xl bg-[#F5F0E8] p-3 overflow-hidden">
                            <QRCode
                                value={targetUrl}
                                size={256}
                                bgColor="#F5F0E8"
                                fgColor="#0B0B0B"
                                style={{ height: "100%", width: "100%" }}
                                level="H"
                            />
                            {/* Animated scan line */}
                            <motion.div
                                aria-hidden
                                initial={{ y: "-100%", opacity: 0 }}
                                animate={inView ? { y: ["-100%", "120%", "-100%"], opacity: [0, 1, 0] } : {}}
                                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--burger-orange)] to-transparent shadow-[0_0_20px_var(--burger-orange)]"
                            />
                            {/* Center logo overlay (small) */}
                            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                <div className="h-12 w-12 grid place-items-center rounded-2xl ember-gradient shadow-lg ring-4 ring-[#F5F0E8]">
                                    <Scan size={20} strokeWidth={2.5} className="text-black" />
                                </div>
                            </div>
                        </div>

                        {/* Bottom CTA strip */}
                        <div className="mt-5 text-center">
                            <p className="font-display text-3xl tracking-tight">
                                SCAN TO <span className="ember-text">ORDER</span>
                            </p>
                            <p className="text-[10px] font-mono text-white/45 tracking-widest mt-1">
                                grill-bros.gr/menu
                            </p>
                        </div>

                        {/* Corner ticks */}
                        {["top-left","top-right","bottom-left","bottom-right"].map((p) => (
                            <span key={p} className={`absolute h-4 w-4 border-[var(--burger-orange)]/60 ${
                                p === "top-left" ? "top-3 left-3 border-t-2 border-l-2" :
                                p === "top-right" ? "top-3 right-3 border-t-2 border-r-2" :
                                p === "bottom-left" ? "bottom-3 left-3 border-b-2 border-l-2" :
                                "bottom-3 right-3 border-b-2 border-r-2"
                            }`} />
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default QRScanSection;
